import { ShieldOff } from 'lucide-react';

import { STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-586 — "this account cannot reach any environment on this console."
//
// Rendered by `AppShell` in place of the routed page when every environment in a
// multi-environment console has refused this tenant with `403 FORBIDDEN_TENANT`.
// Before it, that operator landed on `environments[0]` and was shown that one
// environment's refusal as if it were a page error — "Could not load the
// workspace" — which reads as a broken console rather than as the true and
// complete answer: none of these deployments admits this tenant.
//
// IT LIVES IN THE CONTENT COLUMN, NOT OVER THE WHOLE SHELL, deliberately. The
// top bar stays, and with it the signed-in block — which is where the operator's
// most likely next step lives (sign out and back in as the account that IS
// permitted). A full-viewport takeover would put the one useful control out of
// reach of the message that tells them to use it.
//
// IT NAMES NO ENVIRONMENT. Every one of them refused this tenant, so naming any
// would describe the fleet to someone the fleet has said no to — the disclosure
// FD-586 exists to stop. Nor does it say which tenant was refused: the server
// never echoes that back on a refusal (a 403 carries a code, not a verdict about
// the caller), and inventing it here from the MSAL account would be a guess
// presented as a fact.

export default function NoEnvironmentAccess() {
  return (
    <section
      role="alert"
      aria-labelledby="no-environment-access-title"
      className="mx-auto mt-16 flex max-w-[560px] flex-col items-start gap-3 px-6 py-6"
      style={{
        backgroundColor: SURFACE.base,
        border: `1px solid ${STROKE.base}`,
      }}
    >
      <ShieldOff size={22} strokeWidth={1.5} color={TEXT.secondary} aria-hidden="true" />
      <h1
        id="no-environment-access-title"
        className="text-[18px] font-semibold"
        style={{ color: TEXT.primary }}
      >
        This account has no access to any environment on this console
      </h1>
      <p className="text-[14px] leading-relaxed" style={{ color: TEXT.secondary }}>
        Each environment checks the signed-in account&rsquo;s organisation against its own
        list of permitted tenants, and none of them admits this one. If you expected
        access, check that you signed in with your organisation&rsquo;s account, or ask
        your AirBridge administrator to add your tenant to your environment.
      </p>
    </section>
  );
}
